import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  ScrollText, 
  User, 
  FileText, 
  Settings, 
  LogIn,
  Upload,
  Trash2,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  Phone,
  AlertTriangle,
  CheckCircle,
  XCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const actionIcons = {
  login: LogIn,
  register: User,
  upload: Upload,
  delete: Trash2,
  query: MessageSquare,
  create: FileText,
  update: Settings,
  whatsapp_query: Phone,
  override_request_created: AlertTriangle,
  override_request_approved: CheckCircle,
  override_request_denied: XCircle,
  default: ScrollText
};

const actionColors = {
  login: 'text-blue-400 bg-blue-500/10',
  register: 'text-emerald-400 bg-emerald-500/10',
  upload: 'text-purple-400 bg-purple-500/10',
  delete: 'text-red-400 bg-red-500/10',
  query: 'text-amber-400 bg-amber-500/10',
  create: 'text-emerald-400 bg-emerald-500/10',
  update: 'text-blue-400 bg-blue-500/10',
  whatsapp_query: 'text-green-400 bg-green-500/10',
  override_request_created: 'text-orange-400 bg-orange-500/10',
  override_request_approved: 'text-emerald-400 bg-emerald-500/10',
  override_request_denied: 'text-red-400 bg-red-500/10',
  default: 'text-muted-foreground bg-secondary'
};

const actionFilters = [
  { value: 'all', label: 'All Activity' },
  { value: 'whatsapp', label: 'WhatsApp Activity' },
  { value: 'override', label: 'Override Requests' },
  { value: 'login', label: 'Logins' },
  { value: 'upload', label: 'Uploads' },
];

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState('all');
  const limit = 20;

  useEffect(() => {
    fetchLogs();
  }, [page, filter]);

  const fetchLogs = async () => {
    setLoading(true);
    try {
      let url = `${API}/audit-logs?limit=${limit}&offset=${page * limit}`;
      if (filter !== 'all') {
        url += `&action_filter=${filter}`;
      }
      const response = await axios.get(url);
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Failed to fetch audit logs:', error);
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (action) => {
    const Icon = actionIcons[action] || actionIcons.default;
    return Icon;
  };

  const getColorClass = (action) => {
    return actionColors[action] || actionColors.default;
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString('en-CA', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl lg:text-3xl font-bold text-foreground">Audit Logs</h1>
        <p className="text-muted-foreground mt-1">
          Complete activity history for your organisation
        </p>
      </div>

      {/* Filter Tabs */}
      <div className="flex gap-2 flex-wrap">
        {actionFilters.map((f) => (
          <Button
            key={f.value}
            variant={filter === f.value ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setFilter(f.value); setPage(0); }}
            className={filter === f.value ? '' : 'text-muted-foreground'}
          >
            {f.value === 'whatsapp' && <Phone className="w-4 h-4 mr-1" />}
            {f.value === 'override' && <AlertTriangle className="w-4 h-4 mr-1" />}
            {f.label}
          </Button>
        ))}
      </div>

      {/* Logs */}
      <Card>
        <CardHeader>
          <CardTitle>Activity Log</CardTitle>
          <CardDescription>
            {total} total events {filter !== 'all' && `(filtered: ${filter})`}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12">
              <ScrollText className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">No audit logs yet</p>
            </div>
          ) : (
            <div className="space-y-2">
              {logs.map((log) => {
                const Icon = getIcon(log.action);
                const colorClass = getColorClass(log.action);
                
                return (
                  <div
                    key={log.id}
                    className="flex items-center gap-4 p-4 rounded-lg bg-secondary/30 hover:bg-secondary/50 transition-colors"
                    data-testid={`audit-log-${log.id}`}
                  >
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClass}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-foreground">
                          {log.user_id?.startsWith('whatsapp:') 
                            ? `WhatsApp User (${log.user_id.replace('whatsapp:', '')})` 
                            : log.user_name || log.user_id}
                        </span>
                        <Badge variant="outline" className="text-xs capitalize">
                          {log.action?.replace(/_/g, ' ')}
                        </Badge>
                        <span className="text-muted-foreground">{log.resource_type}</span>
                      </div>
                      {log.details && Object.keys(log.details).length > 0 && (
                        <div className="mt-1 text-sm text-muted-foreground">
                          {log.action === 'query' && log.details.project_name && (
                            <p>
                              <span className="text-foreground">Project:</span> {log.details.project_name}
                            </p>
                          )}
                          {log.action === 'query' && log.details.documents_searched && (
                            <p>
                              <span className="text-foreground">Documents:</span> {log.details.documents_searched.join(', ')}
                            </p>
                          )}
                          {log.action === 'query' && log.details.question_preview && (
                            <p className="italic">
                              "{log.details.question_preview}"
                            </p>
                          )}
                          {log.action !== 'query' && log.details.filename && (
                            <p>
                              <span className="text-foreground">File:</span> {log.details.filename}
                            </p>
                          )}
                          {log.action !== 'query' && log.details.email && (
                            <p>
                              <span className="text-foreground">User:</span> {log.details.email}
                            </p>
                          )}
                          {log.action !== 'query' && log.details.role && (
                            <p>
                              <span className="text-foreground">Role:</span> {log.details.role}
                            </p>
                          )}
                          {/* WhatsApp Query Details */}
                          {log.action === 'whatsapp_query' && (
                            <>
                              <p>
                                <span className="text-foreground">Phone:</span> {log.details.phone_number}
                              </p>
                              {log.details.query && (
                                <p className="italic text-foreground/80">
                                  "{log.details.query}"
                                </p>
                              )}
                              {log.details.route_locations?.length > 0 && (
                                <p>
                                  <span className="text-foreground">Route:</span> {log.details.route_locations.join(' → ')}
                                </p>
                              )}
                              {log.details.requires_override && (
                                <Badge variant="destructive" className="mt-1">Override Required</Badge>
                              )}
                            </>
                          )}
                          {/* Override Request Details */}
                          {log.action?.includes('override_request') && (
                            <>
                              {log.details.route && (
                                <p>
                                  <span className="text-foreground">Route:</span> {log.details.route}
                                </p>
                              )}
                              {log.details.driver_phone && (
                                <p>
                                  <span className="text-foreground">Driver:</span> {log.details.driver_phone}
                                </p>
                              )}
                              {log.details.reason && (
                                <p>
                                  <span className="text-foreground">Reason:</span> {log.details.reason}
                                </p>
                              )}
                              {log.details.response_note && (
                                <p>
                                  <span className="text-foreground">Manager Note:</span> {log.details.response_note}
                                </p>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="text-right text-sm text-muted-foreground whitespace-nowrap">
                      {formatDate(log.created_at)}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  data-testid="prev-page-btn"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <span className="text-sm text-muted-foreground px-2">
                  Page {page + 1} of {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  data-testid="next-page-btn"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
